import Ticket from 'lucide-react-native/icons/ticket';
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Fonts, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { prettyVenue } from '@todo/core/city-listings';
import { showHasManyVenues, type ShowDate } from '@todo/core/event-show';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { currencySymbol, formatDateLong } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import type { DbEvent } from '@todo/core/supabase';

/** Dates rendered before the "show all" toggle. */
const INITIAL_DATES = 10;

function formatPrice(date: ShowDate): string | null {
  if (date.min_price == null) return null;
  const symbol = currencySymbol(date.currency);
  return `${symbol}${date.min_price}`;
}

/**
 * "Dates & tickets" on a recurring show's page: one row per upcoming date with
 * its own price and ticket link (each date is its own Fnac product). Shared by
 * the native and web variants of app/[lang]/events/[id]. Each CTA goes through
 * openExternal with that date's row id, so affiliate clicks stay attributable
 * per date (see AGENTS.md → Analytics).
 */
export function ShowDates({ event, dates }: { event: DbEvent; dates: ShowDate[] }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const manyVenues = showHasManyVenues(dates);
  const shown = expanded ? dates : dates.slice(0, INITIAL_DATES);

  return (
    <View style={styles.root}>
      <Text
        role="heading"
        aria-level={2}
        style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
        {t('events.datesHeading')}
      </Text>
      {shown.map((date) => {
        const price = formatPrice(date);
        return (
          <View key={date.id} style={[styles.row, { borderColor: theme.border }]}>
            <View style={styles.info}>
              <Text style={[styles.date, { color: theme.text }]}>
                {formatDateLong(date.start_ts, date.tz, locale)}
              </Text>
              {manyVenues && date.venue_name ? (
                <Text style={[styles.facts, { color: theme.mutedForeground }]}>
                  {prettyVenue(date.venue_name)}
                </Text>
              ) : null}
            </View>
            {price ? (
              <Text style={[styles.price, { color: theme.gold }]}>{price}</Text>
            ) : null}
            {date.ticket_url ? (
              <Button
                variant="outline"
                size="sm"
                leadingIcon={<Ticket size={14} color={theme.text} />}
                accessibilityLabel={`${t('events.tickets')} — ${formatDateLong(date.start_ts, date.tz, locale)}`}
                onPress={() =>
                  openExternal(date.ticket_url as string, {
                    kind: 'event',
                    source: date.source,
                    id: date.id,
                    title: localizedTitle(event, locale),
                    city: date.city_id,
                    locale,
                  })
                }>
                {t('events.tickets')}
              </Button>
            ) : null}
          </View>
        );
      })}
      {dates.length > INITIAL_DATES ? (
        <Button variant="ghost" size="sm" onPress={() => setExpanded((v) => !v)}>
          {expanded ? t('events.showFewerDates') : t('events.showAllDates', { count: dates.length })}
        </Button>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: Spacing.one,
  },
  heading: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: Spacing.one,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
    paddingVertical: Spacing.two,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  info: {
    flex: 1,
    gap: 2,
  },
  date: {
    fontSize: 15,
  },
  facts: {
    fontSize: 13,
  },
  price: {
    fontSize: 15,
    fontWeight: '700',
  },
});
